import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  CrossCloseIcon,
  DeleteIcon,
  EditIcon,
  FailedIcon,
  OpenDemoIcon,
  SuccessIcon,
} from '@common/utils/icons/icons';
import { useTranslation } from 'react-i18next';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import { useAppDispatch, useAppSelector } from '../../store';
import CustomLabelTextTable from '../misc/CustomLabelTextTable';
import ListAgreement from '../../models/general/agreements/list-agreement';
import { dateToStringFormat } from '@common/utils/date';
import { selectAgreementsData } from '../../store/general/general-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { deleteAgreementById, getAgreementById } from '../../store/general/general-actions';
import { resetAgreementsRestState } from '../../store/general/general-slice';
import CustomConfirmAbortDialog from '../misc/CustomConfirmAbortDialog';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import AgreementsForm from './AgreementsForm';
import AgreementAcceptances from './AgreementAcceptances';
import AgreementContent from './AgreementContent';
import AgreementPopups from './AgreementPopups';

const AgreementInfo: React.FC<{ agreement?: ListAgreement; onClose: () => void }> = ({
  agreement,
  onClose,
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const agreementsData = useAppSelector((state) => selectAgreementsData(state));

  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);
  const [isAgreementsFormOpen, setIsAgreementsFormOpen] = useState(false);
  const [isDemoOpen, setIsDemoOpen] = useState(false);

  useEffect(() => {
    if (agreement?.id != null && agreement.id !== agreementsData.currentDetails?.id) {
      dispatch(resetAgreementsRestState({ getAgreementStatus: HttpFetchStatus.Idle }));
    }
  }, [agreement?.id, agreementsData.currentDetails?.id, dispatch]);

  useEffect(() => {
    if (agreement?.id != null && agreementsData.rest.getAgreementStatus === HttpFetchStatus.Idle) {
      dispatch(getAgreementById(agreement.id));
    }
  }, [agreement?.id, agreementsData.rest.getAgreementStatus, dispatch]);

  if (agreement == null) {
    return null;
  }

  const openConfirmDeleteDialogClickHandler = () => {
    setIsConfirmDeleteDialogOpen(true);
  };

  const closeConfirmDeleteDialogHandler = () => {
    setIsConfirmDeleteDialogOpen(false);
  };

  const openEditAgreementFormClickHandler = () => {
    setIsAgreementsFormOpen(true);
  };

  const closeAgreementsFormHandler = () => {
    setIsAgreementsFormOpen(false);
  };

  const openDemoViewClickHandler = () => {
    setIsDemoOpen(true);
  };

  const closeDemoViewHandler = () => {
    setIsDemoOpen(false);
  };

  const deleteAgreementHandler = () => {
    dispatch(deleteAgreementById(agreement.id));
  };

  const agreementFields = [
    {
      label: t('table-header-agreement-order'),
      text: agreement.order + 1,
    },
    {
      label: t('table-header-agreement-title-key'),
      text: t(agreement.titleKey),
    },
    {
      label: t('table-header-agreement-valid-from'),
      text: dateToStringFormat(agreement.validFrom),
    },
    {
      label: t('table-header-agreement-enabled'),
      text: agreement.enabled ? <SuccessIcon color="success" /> : <FailedIcon color="error" />,
    },
  ];

  if (agreement.instructionsKey) {
    agreementFields.push({
      label: t('table-header-agreement-instructions-key'),
      text: t(agreement.instructionsKey),
    });
  }

  if (agreement.agreementKey) {
    agreementFields.push({
      label: t('table-header-agreement-agreement-key'),
      text: t(agreement.agreementKey),
    });
  }

  return (
    <React.Fragment>
      <Stack spacing={2} sx={{ p: { xs: 1, sm: undefined } }}>
        <Stack
          direction="row"
          sx={(theme) => ({ height: theme.spacing(5) })}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              <IconButton onClick={onClose}>
                <CrossCloseIcon />
              </IconButton>
            </Box>
            <Typography variant="h2">{agreement.name}</Typography>
          </Stack>
          <Button startIcon={<EditIcon />} onClick={openEditAgreementFormClickHandler}>
            {t('button-edit')}
          </Button>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip label={t('agreement-type-' + agreement.agreementType)} variant="outlined" />
          <TooltipIconButton
            tooltip={t('info-tooltip-open-demo')}
            onClick={openDemoViewClickHandler}
            icon={<OpenDemoIcon data-testid="demo-icon" />}
            color="primary"
          />
          <TooltipIconButton
            tooltip={t('info-tooltip-delete-agreement')}
            onClick={openConfirmDeleteDialogClickHandler}
            icon={<DeleteIcon data-testid="delete-icon" />}
            color="error"
          />
        </Stack>

        <CustomLabelTextTable rows={agreementFields} />

        <AgreementContent agreement={agreement} />
        <AgreementAcceptances agreement={agreement} />
        {isDemoOpen && (
          <AgreementPopups
            demo={isDemoOpen ? { agreement, onClose: closeDemoViewHandler } : undefined}
          />
        )}
      </Stack>
      <AgreementsForm
        agreement={agreement}
        open={isAgreementsFormOpen}
        onClose={closeAgreementsFormHandler}
        agreementType={agreement.agreementType}
      />
      <CustomConfirmAbortDialog
        open={isConfirmDeleteDialogOpen}
        title={t('title-delete-agreement-' + agreement.agreementType)}
        text={<CustomAlert severity="warning" header={t('warning-deleting-is-permanent')} />}
        confirmButton={t('button-delete')}
        abortButton={t('button-cancel')}
        onAbort={closeConfirmDeleteDialogHandler}
        onConfirm={deleteAgreementHandler}
      />
    </React.Fragment>
  );
};

export default AgreementInfo;
